import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { WalletScreen } from './components/wallet/WalletScreen';
import { ResendMount } from './components/wallet/ResendMount';
import { SplashScreen } from './components/SplashScreen';
import { WebLogin } from './components/WebLogin';
import { NewSeedScreen, SeedImportScreen, WalletSetupScreen } from './components/SeedImportScreen';
import { SecuritySetup } from './components/wallet/SecuritySetup';
import { ReauthFlow } from './components/wallet/ReauthFlow';
import { clearWebSession, readWebSession } from './lib/webSession';
import { setWebUserId } from './lib/sessionUser';
import { fetchMe } from './lib/me';
import { useTelegram } from './hooks/useTelegram';
import { useUiStore } from './store/ui';
import {
  adoptWebSeedKey,
  clearLocalSeed,
  createMnemonic,
  deriveWallet,
  fetchRemoteWallet,
  hasLocalSeed,
  loadStoredSeed,
  getSeed,
  migrateLegacySeed,
  persistImportedSeed,
  persistNewSeed,
  readSeed,
  registerWallet,
  restoreFundedAddresses,
} from './lib/wallet';
import { decideWebGate } from './lib/webGate';
import { consumeWalletSetupOffer } from './lib/walletSetupOffer';
import { ensureWalletAddresses, needsAddressBackfill } from './lib/addressBackfill';

type Phase = 'splash' | 'leaving' | 'app';
type WebGate = 'boot' | 'import' | 'ready' | 'setup' | 'recover' | 'backup';

function importErrorCode(e: unknown): string {
  const msg = String(e);
  if (/pin_required/i.test(msg)) return 'pin_required';
  if (/reauth_required/i.test(msg)) return 'reauth_required';
  if (/seed_mismatch/i.test(msg)) return 'seed_mismatch';
  if (/Failed to fetch|NetworkError|API 5\d\d/i.test(msg)) return 'network';
  return 'failed';
}

/** Expired or rejected web session. Boot must drop it and show login — retrying
 *  /me on 401 leaves the splash up forever. */
function isUnauthorized(e: unknown): boolean {
  return /API 401\b|unauthorized/i.test(String(e));
}

export function App() {
  const tg = useTelegram();

  const inTelegram = !!window.Telegram?.WebApp?.initData;
  const [webToken, setWebToken] = useState<string | null>(() => readWebSession());
  // Require a real session outside Telegram (including DEV).
  const showLogin = !inTelegram && !webToken;
  const uiPreview =
    import.meta.env.DEV && new URLSearchParams(window.location.search).get('ui') === '1';

  const sheetCount = useUiStore((s) => s.sheetCount);
  const [phase, setPhase] = useState<Phase>(() =>
    hasLocalSeed() || !inTelegram ? 'app' : 'splash',
  );
  const [setupActive, setSetupActive] = useState(false);
  const [bootAttempt, setBootAttempt] = useState(0);
  const [webGate, setWebGate] = useState<WebGate>(() =>
    !inTelegram && webToken ? 'boot' : 'ready',
  );
  const [importBusy, setImportBusy] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);
  const [pendingImport, setPendingImport] = useState<string | null>(null);
  const [importReauth, setImportReauth] = useState(false);
  /** Web create hit orphan seed-key rotate — need PIN reauth then retry create. */
  const [createReauth, setCreateReauth] = useState(false);
  const [pendingCreateMnemonic, setPendingCreateMnemonic] = useState<string | null>(null);
  /** Phrase just created or recovered — shown once before the wallet opens. */
  const [backupMnemonic, setBackupMnemonic] = useState<string | null>(null);
  /** Account has no server wallet, so import may go back to the create/import choice. */
  const [walletMissing, setWalletMissing] = useState(false);
  const [webPinSet, setWebPinSet] = useState(false);
  const [creating, setCreating] = useState(false);
  const bootRan = useRef(false);
  /** One create at a time. A second tap must not persist a different phrase. */
  const createLock = useRef(false);
  const queryClient = useQueryClient();

  // Auth credential: Telegram initData, or empty string for web bearer.
  const cred = tg.initData || (webToken ? '' : '');

  useEffect(() => {
    const root = document.getElementById('root');
    if (!root) return;
    const lock = sheetCount > 0 || showLogin;
    root.style.overflowY = lock ? 'hidden' : 'auto';
    // Login is a fixed screen. iOS still pans #root to reveal a focused input.
    if (!showLogin) {
      return () => {
        root.style.overflowY = 'auto';
      };
    }
    root.style.touchAction = 'none';
    const pin = () => {
      if (root.scrollTop !== 0) root.scrollTop = 0;
    };
    pin();
    root.addEventListener('scroll', pin);
    return () => {
      root.removeEventListener('scroll', pin);
      root.style.overflowY = 'auto';
      root.style.touchAction = '';
    };
  }, [sheetCount, showLogin]);

  // Reset boot when a new web session appears.
  useEffect(() => {
    if (inTelegram || !webToken) return;
    bootRan.current = false;
    setWebGate('boot');
    setSetupActive(false);
    setImportReauth(false);
    setCreateReauth(false);
    setPendingImport(null);
    setPendingCreateMnemonic(null);
    setBackupMnemonic(null);
    setWalletMissing(false);
    setBootAttempt((n) => n + 1);
  }, [webToken, inTelegram]);

  const authCredFor = () => (inTelegram ? tg.initData : '');

  const finishWebCreate = async (mnemonic: string, reauthToken?: string) => {
    if (createLock.current) return;
    createLock.current = true;
    setCreating(true);
    const authCred = authCredFor();
    try {
      await persistNewSeed(authCred, mnemonic, reauthToken);
      await registerWallet(authCred, deriveWallet(mnemonic));
      queryClient.invalidateQueries({ queryKey: ['wallet'] });
      setPendingCreateMnemonic(null);
      setCreateReauth(false);
      setWalletMissing(false);
      setBackupMnemonic(mnemonic);
      setWebGate('backup');
    } catch (e) {
      const code = importErrorCode(e);
      if (code === 'reauth_required') {
        setPendingCreateMnemonic(mnemonic);
        setCreateReauth(true);
        return;
      }
      if (code === 'pin_required') {
        setPendingCreateMnemonic(mnemonic);
        setSetupActive(true);
        return;
      }
      // Network / other — retry boot from scratch.
      bootRan.current = false;
      setWebGate(inTelegram ? 'setup' : 'boot');
      setBootAttempt((n) => n + 1);
    } finally {
      createLock.current = false;
      setCreating(false);
    }
  };

  // Wallet bootstrap (TMA + web).
  useEffect(() => {
    if (showLogin) return;
    if (inTelegram && tg.status === 'loading') return;
    if (bootRan.current) return;
    bootRan.current = true;
    let retryTimer: number | undefined;
    const retryBoot = () => {
      bootRan.current = false;
      retryTimer = window.setTimeout(() => setBootAttempt((n) => n + 1), 3_000);
    };

    (async () => {
      let pinSet = false;
      // Web: resolve server user id + PIN before any seed-key write.
      if (!inTelegram && webToken) {
        try {
          const me = await fetchMe('');
          setWebUserId(me.user.id);
          adoptWebSeedKey(me.user.id);
          pinSet = !!me.user.pinSet;
          setWebPinSet(pinSet);
        } catch (e) {
          if (isUnauthorized(e)) {
            clearWebSession();
            setWebUserId(null);
            setWebToken(null);
            return;
          }
          retryBoot();
          return;
        }
        // PIN before POST /seed-key — blocks squat + avoids splash retry loops.
        if (!pinSet) {
          setSetupActive(true);
          setWebGate('boot');
          return;
        }
      }

      const authCred = inTelegram ? tg.initData : '';
      const stored = await loadStoredSeed(authCred || undefined).catch(() => null);

      if (stored) {
        if (authCred || webToken) {
          if (authCred) await migrateLegacySeed(authCred).catch(() => {});
          const remote = await fetchRemoteWallet(authCred).catch(() => undefined);
          if (!inTelegram) {
            const gate = decideWebGate(true, remote);
            // Leftover ciphertext after a reset cannot be opened without the
            // server key. Don't enter the app — recover it, or offer create/import.
            if (gate === 'retry') {
              retryBoot();
              return;
            }
            if (gate === 'recover') {
              setWalletMissing(true);
              setWebGate('recover');
              setPhase('app');
              return;
            }
          } else if (remote === null) {
            // Server wallet gone (e.g. reset) but ciphertext still on device.
            // Re-register if we can still decrypt; otherwise wipe local and offer
            // create/import — do not open the app on a dead seed.
            const m = await getSeed(authCred).catch(() => null);
            if (m) {
              try {
                await registerWallet(authCred, deriveWallet(m));
                await restoreFundedAddresses(authCred, m).catch(() => {});
              } catch {
                retryBoot();
                return;
              }
            } else {
              await clearLocalSeed();
              setWalletMissing(true);
              setWebGate('setup');
              setPhase('app');
              return;
            }
          }
          if (remote !== undefined) {
            queryClient.invalidateQueries({ queryKey: ['wallet'] });
          }
          if (remote && needsAddressBackfill(remote) && (authCred || webToken)) {
            void (async () => {
              const m = await getSeed(authCred).catch(() => null);
              if (m) {
                await ensureWalletAddresses(authCred, m, remote, () =>
                  queryClient.invalidateQueries({ queryKey: ['wallet'] }),
                ).catch(() => {});
              }
            })();
          }
        }
        setWalletMissing(false);
        setWebGate('ready');
        setPhase('app');
        return;
      }

      // No local seed.
      const remote = await fetchRemoteWallet(authCred).catch(() => undefined);

      if (remote === null) {
        // Web always asks. TMA first open still auto-creates; after reset the
        // client sets a session flag so we offer create vs restore instead.
        if (!inTelegram || consumeWalletSetupOffer()) {
          setWalletMissing(true);
          setWebGate('setup');
          setPhase('app');
          return;
        }
        setPhase('splash');
        setWebGate('ready');
        const m = createMnemonic();
        try {
          await persistNewSeed(authCred, m);
        } catch {
          retryBoot();
          return;
        }
        try {
          await registerWallet(authCred, deriveWallet(m));
        } catch {
          retryBoot();
          return;
        }
        queryClient.invalidateQueries({ queryKey: ['wallet'] });
        window.setTimeout(() => setPhase('leaving'), 1800);
        window.setTimeout(() => {
          setPhase('app');
          setSetupActive(true);
        }, 2360);
        return;
      }

      if (remote) {
        // Server wallet exists but this device has no seed → import on web;
        // in TMA CloudStorage usually already had it (fall through to app).
        if (!inTelegram) {
          setWalletMissing(false);
          setWebGate('import');
          setPhase('app');
          return;
        }
        setWebGate('ready');
        setPhase('app');
        queryClient.invalidateQueries({ queryKey: ['wallet'] });
        return;
      }

      // Backend unreachable — retry on web; TMA can open cached UI.
      if (!inTelegram) {
        retryBoot();
        return;
      }
      setWebGate('ready');
      setPhase('app');
    })();

    return () => {
      if (retryTimer !== undefined) window.clearTimeout(retryTimer);
    };
    // finishWebCreate closes over stable setters; boot is re-driven via bootAttempt.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tg.status, tg.initData, bootAttempt, showLogin, inTelegram, webToken, queryClient]);

  const finishImport = async (mnemonic: string, reauthToken?: string) => {
    setImportBusy(true);
    setImportError(null);
    try {
      const authCred = authCredFor();
      const remote = await fetchRemoteWallet(authCred).catch(() => undefined);
      if (remote === undefined) {
        // Drop reauth cover so SeedImportScreen can show the error.
        setImportReauth(false);
        setImportError('network');
        return;
      }
      if (remote) {
        // Existing wallet: reuse the immutable server key — never POST a new one.
        await persistImportedSeed(authCred, mnemonic, remote, reauthToken);
        // Pre-BTC accounts get their bc1q twin registered now that we have the seed.
        if (needsAddressBackfill(remote)) {
          await ensureWalletAddresses(authCred, mnemonic, remote, () =>
            queryClient.invalidateQueries({ queryKey: ['wallet'] }),
          ).catch(() => {});
        }
      } else {
        await persistNewSeed(authCred, mnemonic, reauthToken);
        await registerWallet(authCred, deriveWallet(mnemonic));
      }
      await restoreFundedAddresses(authCred, mnemonic).catch(() => {});
      queryClient.invalidateQueries({ queryKey: ['wallet'] });
      setPendingImport(null);
      setImportReauth(false);
      setWalletMissing(false);
      setWebGate('ready');
      // After reset PIN is wiped — offer security setup (same as first create).
      if (inTelegram) setSetupActive(true);
    } catch (e) {
      const code = importErrorCode(e);
      if (code === 'reauth_required' || code === 'pin_required') {
        setPendingImport(mnemonic);
        if (code === 'pin_required') {
          setImportReauth(false);
          setSetupActive(true);
          setImportError('pin_required');
        } else {
          setImportReauth(true);
        }
        return;
      }
      setImportReauth(false);
      setImportError(code);
    } finally {
      setImportBusy(false);
    }
  };

  const finishBackup = () => {
    setBackupMnemonic(null);
    setWebGate('ready');
    if (inTelegram) {
      setSetupActive(true);
      setPhase('app');
      return;
    }
    setPhase('leaving');
    window.setTimeout(() => setPhase('app'), 560);
  };

  const recoverLocalWallet = async (token: string) => {
    const read = await readSeed('', token);
    if (!read.ok) {
      if (read.reason === 'no_key' || read.reason === 'decrypt' || read.reason === 'missing') {
        await clearLocalSeed();
        setWebGate('setup');
        return;
      }
      bootRan.current = false;
      setWebGate('boot');
      setBootAttempt((n) => n + 1);
      return;
    }
    try {
      await registerWallet('', deriveWallet(read.mnemonic));
      await restoreFundedAddresses('', read.mnemonic).catch(() => {});
      queryClient.invalidateQueries({ queryKey: ['wallet'] });
    } catch {
      bootRan.current = false;
      setWebGate('boot');
      setBootAttempt((n) => n + 1);
      return;
    }
    setWalletMissing(false);
    setBackupMnemonic(read.mnemonic);
    setWebGate('backup');
  };

  const onWebImport = async (mnemonic: string) => {
    // Web requires PIN before seed-key writes. TMA can import immediately;
    // finishImport escalates to reauth if the server demands it.
    if (!inTelegram && !webPinSet) {
      setPendingImport(mnemonic);
      setSetupActive(true);
      setImportError('pin_required');
      return;
    }
    if (!inTelegram) {
      setImportReauth(true);
      setPendingImport(mnemonic);
      return;
    }
    setPendingImport(mnemonic);
    await finishImport(mnemonic);
  };

  if (showLogin && !uiPreview) {
    return (
      <WebLogin
        onSignedIn={() => {
          // Drop previous account cache / in-memory id before adopting the new session.
          setWebUserId(null);
          queryClient.clear();
          setWebToken(readWebSession());
        }}
      />
    );
  }

  // Security setup before boot splash so web PIN-first is visible.
  if (setupActive) {
    return (
      <SecuritySetup
        initData={cred}
        requirePin={!inTelegram}
        onComplete={() => {
          setSetupActive(false);
          setWebPinSet(true);
          queryClient.invalidateQueries({ queryKey: ['me'] });
          if (pendingImport) {
            if (inTelegram) void finishImport(pendingImport);
            else setImportReauth(true);
            return;
          }
          if (pendingCreateMnemonic) {
            if (inTelegram) void finishWebCreate(pendingCreateMnemonic);
            else setCreateReauth(true);
            return;
          }
          if (!inTelegram) {
            // Resume boot (create or import) now that server PIN exists.
            bootRan.current = false;
            setWebGate('boot');
            setBootAttempt((n) => n + 1);
          }
        }}
      />
    );
  }

  if (createReauth && pendingCreateMnemonic) {
    return (
      <div className="push-enter fixed inset-0 z-[60] overflow-y-auto bg-holo">
        <ReauthFlow
          initData={cred}
          biometricEnabled={false}
          onSuccess={(token) => {
            setCreateReauth(false);
            void finishWebCreate(pendingCreateMnemonic, token);
          }}
          onCancel={() => {
            setCreateReauth(false);
            setPendingCreateMnemonic(null);
            bootRan.current = false;
            setWebGate(inTelegram ? 'setup' : 'boot');
            setBootAttempt((n) => n + 1);
          }}
        />
      </div>
    );
  }

  if (!inTelegram && webGate === 'boot') {
    return <SplashScreen leaving={false} />;
  }

  if (!inTelegram && webGate === 'recover') {
    return (
      <div className="push-enter fixed inset-0 z-[60] overflow-y-auto bg-holo">
        <ReauthFlow
          initData={cred}
          biometricEnabled={false}
          onSuccess={(token) => {
            void recoverLocalWallet(token);
          }}
          onCancel={() => setWebGate('setup')}
        />
      </div>
    );
  }

  if (webGate === 'backup' && backupMnemonic) {
    return <NewSeedScreen mnemonic={backupMnemonic} onContinue={finishBackup} />;
  }

  if (webGate === 'setup') {
    return (
      <WalletSetupScreen
        busy={creating}
        onCreate={() => {
          void finishWebCreate(createMnemonic());
        }}
        onImport={() => setWebGate('import')}
      />
    );
  }

  // Keep a cover while import runs — clearing importReauth before finishImport
  // settles used to fall through to SeedImportScreen for a frame (or longer).
  if (importBusy && pendingImport) {
    return <SplashScreen leaving={false} />;
  }

  if (importReauth && pendingImport) {
    return (
      <div className="push-enter fixed inset-0 z-[60] overflow-y-auto bg-holo">
        <ReauthFlow
          initData={cred}
          biometricEnabled={false}
          onSuccess={(token) => {
            void finishImport(pendingImport, token);
          }}
          onCancel={() => {
            setImportReauth(false);
            setPendingImport(null);
            setImportBusy(false);
          }}
        />
      </div>
    );
  }

  if (webGate === 'import') {
    return (
      <SeedImportScreen
        busy={importBusy}
        error={importError}
        onImport={onWebImport}
        onBack={walletMissing ? () => setWebGate('setup') : undefined}
      />
    );
  }

  return (
    <>
      <WalletScreen />
      <ResendMount />
      {phase !== 'app' && <SplashScreen leaving={phase === 'leaving'} />}
    </>
  );
}
